import React, { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';

const StripePaymentForm = ({ amount, onPaymentSuccess, onPaymentError, bookingData, baseAmount, serviceFee }) => {
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [cardNumberElement, setCardNumberElement] = useState(null);
  const [cardExpiryElement, setCardExpiryElement] = useState(null);
  const [cardCvcElement, setCardCvcElement] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cardNumberFocus, setCardNumberFocus] = useState(false);
  const [cardExpiryFocus, setCardExpiryFocus] = useState(false);
  const [cardCvcFocus, setCardCvcFocus] = useState(false);

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        // Get Stripe config from Firebase Function
        const functions = getFunctions();
        const getStripeConfig = httpsCallable(functions, 'getStripeConfig');
        const result = await getStripeConfig();
        const config = result.data;

        console.log('Stripe config loaded:', {
          hasPublishableKey: !!config.publishableKey
        });

        if (!config.publishableKey) {
          console.error('Stripe configuration missing publishable key');
          setError('Payment system configuration error. Please contact support.');
          return;
        }

        // Initialize Stripe
        const stripeInstance = await loadStripe(config.publishableKey);
        setStripe(stripeInstance);

        // Create card elements with better styling
        const elementsInstance = stripeInstance.elements();

        const elementStyles = {
          base: {
            fontSize: '16px',
            color: '#111827',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontWeight: '500',
            '::placeholder': {
              color: '#9ca3af',
            },
            ':focus': {
              color: '#111827',
            },
          },
          invalid: {
            color: '#dc2626',
            iconColor: '#dc2626',
          },
          complete: {
            color: '#059669',
            iconColor: '#059669',
          },
        };

        // Create separate elements for better UX
        const cardNumberElementInstance = elementsInstance.create('cardNumber', {
          style: elementStyles,
          placeholder: '1234 5678 9012 3456',
        });

        const cardExpiryElementInstance = elementsInstance.create('cardExpiry', {
          style: elementStyles,
          placeholder: 'MM / YY',
        });

        const cardCvcElementInstance = elementsInstance.create('cardCvc', {
          style: elementStyles,
          placeholder: 'CVC',
        });

        // Add event listeners for focus states
        cardNumberElementInstance.on('focus', () => setCardNumberFocus(true));
        cardNumberElementInstance.on('blur', () => setCardNumberFocus(false));
        cardExpiryElementInstance.on('focus', () => setCardExpiryFocus(true));
        cardExpiryElementInstance.on('blur', () => setCardExpiryFocus(false));
        cardCvcElementInstance.on('focus', () => setCardCvcFocus(true));
        cardCvcElementInstance.on('blur', () => setCardCvcFocus(false));

        // Store elements first
        setCardNumberElement(cardNumberElementInstance);
        setCardExpiryElement(cardExpiryElementInstance);
        setCardCvcElement(cardCvcElementInstance);
        setElements(elementsInstance);
        setLoading(false);

        console.log('Stripe payment form initialized successfully');
      } catch (err) {
        console.error('Failed to initialize Stripe:', err);
        console.error('Error details:', {
          message: err.message,
          code: err.code,
        });
        setError(`Failed to initialize payment form: ${err.message || 'Unknown error'}. Please refresh the page.`);
        setLoading(false);
      }
    };

    initializeStripe();
  }, []);

  // Mount elements after DOM is ready
  useEffect(() => {
    if (!loading && cardNumberElement && cardExpiryElement && cardCvcElement) {
      // Wait for next tick to ensure DOM is rendered
      const timer = setTimeout(() => {
        try {
          cardNumberElement.mount('#card-number-element');
          cardExpiryElement.mount('#card-expiry-element');
          cardCvcElement.mount('#card-cvc-element');
          console.log('Stripe elements mounted successfully');
        } catch (err) {
          console.error('Failed to mount Stripe elements:', err);
          setError(`Failed to initialize payment form: ${err.message || 'Unknown error'}. Please refresh the page.`);
        }
      }, 0);

      return () => {
        clearTimeout(timer);
        // Cleanup: unmount elements when component unmounts
        try {
          cardNumberElement.unmount();
          cardExpiryElement.unmount();
          cardCvcElement.unmount();
        } catch (err) {
          // Elements may already be unmounted
          console.log('Element cleanup:', err.message);
        }
      };
    }
  }, [loading, cardNumberElement, cardExpiryElement, cardCvcElement]);

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!stripe || !cardNumberElement) {
      setError('Payment form not ready. Please try again.');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Create payment intent on the server
      const functions = getFunctions();
      const createPaymentIntent = httpsCallable(functions, 'createPaymentIntent');

      const paymentIntentResult = await createPaymentIntent({
        amount: amount,
        bookingData: bookingData,
      });

      const { clientSecret } = paymentIntentResult.data;

      // Confirm the payment with the card element
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardNumberElement,
            billing_details: {
              name: bookingData.name,
            },
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent.status === 'succeeded') {
        // Confirm payment with server
        const confirmPayment = httpsCallable(functions, 'confirmPayment');
        const confirmResult = await confirmPayment({
          paymentIntentId: paymentIntent.id,
          bookingData: bookingData,
        });

        if (confirmResult.data && confirmResult.data.success) {
          onPaymentSuccess(confirmResult.data);
        } else {
          throw new Error('Payment confirmation failed');
        }
      } else {
        throw new Error('Payment did not complete successfully');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment processing failed. Please try again.');
      if (onPaymentError) {
        onPaymentError(err);
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <form onSubmit={handlePayment}>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>

        {loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            color: '#6b7280'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              marginBottom: '16px'
            }} />
            <p style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>Loading secure payment form...</p>
          </div>
        ) : (
          <>
            {/* Card Number */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'flex',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px',
                alignItems: 'center',
                gap: '6px'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
                Card Number
              </label>
              <div
                style={{
                  border: cardNumberFocus ? '2px solid #3b82f6' : '2px solid #d1d5db',
                  borderRadius: '8px',
                  padding: '14px 16px',
                  backgroundColor: '#ffffff',
                  transition: 'all 0.2s ease',
                  boxShadow: cardNumberFocus ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none'
                }}
              >
                <div id="card-number-element"></div>
              </div>
            </div>

            {/* Expiry and CVC */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              {/* Expiry Date */}
              <div>
                <label style={{
                  display: 'flex',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  Expiry
                </label>
                <div
                  style={{
                    border: cardExpiryFocus ? '2px solid #3b82f6' : '2px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '14px 16px',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.2s ease',
                    boxShadow: cardExpiryFocus ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none'
                  }}
                >
                  <div id="card-expiry-element"></div>
                </div>
              </div>

              {/* CVC */}
              <div>
                <label style={{
                  display: 'flex',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '8px',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                    <path d="M6 14h3M13 14h3"/>
                  </svg>
                  CVC
                </label>
                <div
                  style={{
                    border: cardCvcFocus ? '2px solid #3b82f6' : '2px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '14px 16px',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.2s ease',
                    boxShadow: cardCvcFocus ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none'
                  }}
                >
                  <div id="card-cvc-element"></div>
                </div>
              </div>
            </div>
          </>
        )}

        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px'
          }}>
            <p style={{ fontSize: '14px', color: '#dc2626', margin: 0 }}>{error}</p>
          </div>
        )}

        <div style={{
          backgroundColor: '#f3f4f6',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px'
        }}>
          {baseAmount !== undefined && serviceFee !== undefined ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span style={{ fontSize: '15px', color: '#6b7280' }}>Parking Fee:</span>
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                  ${baseAmount.toFixed(2)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', marginBottom: '12px' }}>
                <span style={{ fontSize: '15px', color: '#6b7280' }}>Service Fee:</span>
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                  ${serviceFee.toFixed(2)}
                </span>
              </div>
              <div style={{ borderTop: '2px solid #d1d5db', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '18px', fontWeight: '700', color: '#111827' }}>Total Amount:</span>
                <span style={{ fontSize: '24px', fontWeight: '700', color: '#059669' }}>
                  ${amount.toFixed(2)}
                </span>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>Total Amount:</span>
              <span style={{ fontSize: '24px', fontWeight: '700', color: '#059669' }}>
                ${amount.toFixed(2)}
              </span>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={processing || !stripe || !cardNumberElement || loading}
          style={{
            width: '100%',
            padding: '16px 24px',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '18px',
            border: 'none',
            cursor: (processing || !stripe || !cardNumberElement || loading) ? 'not-allowed' : 'pointer',
            background: (processing || !stripe || !cardNumberElement || loading) ? '#9ca3af' : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            color: 'white',
            transition: 'all 0.2s ease',
            boxShadow: (processing || !stripe || !cardNumberElement || loading) ? 'none' : '0 4px 6px rgba(0,0,0,0.1)',
          }}
          onMouseEnter={(e) => {
            if (!processing && stripe && cardNumberElement && !loading) {
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.15)';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = (processing || !stripe || !cardNumberElement || loading) ? 'none' : '0 4px 6px rgba(0,0,0,0.1)';
          }}
        >
          {processing ? (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <div style={{
                width: '20px',
                height: '20px',
                border: '3px solid rgba(255,255,255,0.3)',
                borderTop: '3px solid white',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }} />
              Processing Payment...
            </span>
          ) : `Pay $${amount.toFixed(2)}`}
        </button>

        <div style={{
          marginTop: '20px',
          padding: '16px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          textAlign: 'center',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
            <svg style={{ width: '18px', height: '18px', color: '#059669' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
              Secure Payment
            </span>
          </div>
          <p style={{ margin: 0, fontSize: '12px', color: '#6b7280', lineHeight: '1.5' }}>
            Your payment information is encrypted and secure. Powered by Stripe.
          </p>
        </div>
      </form>
    </div>
  );
};

export default StripePaymentForm;
